// Create + edit form for a signup_pages row. /signups/new is create
// mode, /signups/:id is edit mode. The per-signup welcome-email
// customization is a single "top message" HTML field that slots
// into the centralized template at /settings/welcome-email.

import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { signupPagesApi } from '../lib/api';
import type { SignupPage } from '@shared/types';

const HOMEPAGE_SLUG = 'homepage';

interface FormState {
  slug: string;
  title: string;
  headline: string;
  subhead: string;
  body_md: string;
  fineprint: string;
  cta_label: string;
  success_message: string;
  welcome_top_message_html: string;
  active: boolean;
}

const EMPTY: FormState = {
  slug: '',
  title: '',
  headline: '',
  subhead: '',
  body_md: '',
  fineprint: '',
  cta_label: 'Subscribe',
  success_message: '',
  welcome_top_message_html: '',
  active: true,
};

function fromPage(p: SignupPage): FormState {
  return {
    slug: p.slug,
    title: p.title,
    headline: p.headline ?? '',
    subhead: p.subhead ?? '',
    body_md: p.body_md ?? '',
    fineprint: p.fineprint ?? '',
    cta_label: p.cta_label ?? 'Subscribe',
    success_message: p.success_message ?? '',
    welcome_top_message_html: p.welcome_top_message_html ?? '',
    active: p.active,
  };
}

function nullify(s: string): string | null {
  return s.trim().length === 0 ? null : s;
}

export default function SignupPageEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [page, setPage] = useState<SignupPage | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (isCreate) return;
    setLoading(true);
    signupPagesApi
      .get(id!)
      .then(({ page }) => {
        setPage(page);
        setForm(fromPage(page));
        setEmailOpen(
          !!(
            page.welcome_top_message_html ||
            page.welcome_subject ||
            page.welcome_html ||
            page.welcome_text
          ),
        );
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isCreate]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (isCreate) {
        const res = await signupPagesApi.create({
          slug: form.slug.trim(),
          title: form.title.trim(),
          headline: nullify(form.headline),
          subhead: nullify(form.subhead),
          body_md: nullify(form.body_md),
          fineprint: nullify(form.fineprint),
          cta_label: form.cta_label.trim() || 'Subscribe',
          success_message: nullify(form.success_message),
          welcome_top_message_html: nullify(form.welcome_top_message_html),
          active: form.active,
        });
        navigate(`/signups/${res.id}`, { replace: true });
      } else {
        const res = await signupPagesApi.update(id!, {
          slug: form.slug.trim(),
          title: form.title.trim(),
          headline: nullify(form.headline),
          subhead: nullify(form.subhead),
          body_md: nullify(form.body_md),
          fineprint: nullify(form.fineprint),
          cta_label: form.cta_label.trim() || 'Subscribe',
          success_message: nullify(form.success_message),
          welcome_top_message_html: nullify(form.welcome_top_message_html),
          active: form.active,
          expected_updated_at: page!.updated_at,
        });
        // Refresh local state with server-confirmed updated_at.
        setPage((p) => (p ? { ...p, ...form, updated_at: res.updated_at } : p));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const isHomepage = form.slug === HOMEPAGE_SLUG;
  const publicUrl = isHomepage
    ? 'https://penobscotmorning.com/'
    : form.slug
    ? `https://penobscotmorning.com/signup/${form.slug}`
    : null;
  const headerTitle = isCreate
    ? 'New signup page'
    : isHomepage
    ? 'Homepage'
    : form.title || 'Untitled';

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-2xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <button
          onClick={() => navigate('/signups')}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> All signup pages
        </button>

        <header className="mb-6">
          <h1 className="font-display text-2xl sm:text-3xl text-slate-900 break-words">{headerTitle}</h1>
          {!isCreate && publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline mt-1 break-all"
            >
              <ExternalLink size={12} className="flex-shrink-0" /> {publicUrl}
            </a>
          )}
        </header>

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <form onSubmit={save} className="space-y-4">
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

            {!isHomepage && (
              <Field label="Slug" hint="lowercase a–z, 0–9, hyphens. URL: /signup/<slug>">
                <input
                  type="text"
                  required
                  pattern="^[a-z0-9][a-z0-9-]{0,62}$"
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value)}
                  className="input"
                  placeholder="bagelcentral"
                />
              </Field>
            )}

            <Field label="Title" hint={isHomepage ? 'Browser tab' : 'Browser tab + admin label'}>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                className="input"
                placeholder="Bagel Central — subscribe"
              />
            </Field>

            {!isHomepage && (
              <Field label="Headline" hint="Big H1 on the page (defaults to title)">
                <input
                  type="text"
                  value={form.headline}
                  onChange={(e) => set('headline', e.target.value)}
                  className="input"
                  placeholder="Get the Penobscot Morning brief"
                />
              </Field>
            )}

            <Field label={isHomepage ? 'Tagline' : 'Subhead'} hint={isHomepage ? 'Shown under the logo' : undefined}>
              <input
                type="text"
                value={form.subhead}
                onChange={(e) => set('subhead', e.target.value)}
                className="input"
                placeholder={isHomepage ? 'A morning roundup of community news…' : 'Free, weekday mornings.'}
              />
            </Field>

            {!isHomepage && (
              <Field label="Body" hint="Plain text, blank lines separate paragraphs">
                <textarea
                  rows={5}
                  value={form.body_md}
                  onChange={(e) => set('body_md', e.target.value)}
                  className="input"
                  placeholder="A short paragraph or two of pitch copy."
                />
              </Field>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Submit button label">
                <input
                  type="text"
                  value={form.cta_label}
                  onChange={(e) => set('cta_label', e.target.value)}
                  className="input"
                  placeholder="Subscribe"
                />
              </Field>

              {!isHomepage && (
                <Field label="Success message" hint="Banner shown after submit">
                  <input
                    type="text"
                    value={form.success_message}
                    onChange={(e) => set('success_message', e.target.value)}
                    className="input"
                    placeholder="Thanks! Check your inbox."
                  />
                </Field>
              )}
            </div>

            {isHomepage && (
              <Field label="Fineprint" hint="Small note under the form">
                <input
                  type="text"
                  value={form.fineprint}
                  onChange={(e) => set('fineprint', e.target.value)}
                  className="input"
                  placeholder="Delivered each weekday morning. Free, and you can unsubscribe any time."
                />
              </Field>
            )}

            <div>
              <button
                type="button"
                onClick={() => setEmailOpen((o) => !o)}
                className="text-sm text-slate-700 hover:text-slate-900 flex items-center gap-1 font-medium"
              >
                {emailOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Welcome email — top message
              </button>
              {emailOpen && (
                <div className="mt-3 space-y-3 border-l-2 border-slate-200 pl-4">
                  <p className="text-xs text-slate-500">
                    Optional HTML inserted at the very top of the welcome
                    email body, above the greeting. The rest of the email
                    comes from the centralized template at{' '}
                    <Link to="/settings/welcome-email" className="text-blue-700 underline">
                      Settings → Welcome Email
                    </Link>
                    . Supports merge tokens like <code>{'{{first_name|friend}}'}</code>.
                    Leave blank to use the centralized template as-is.
                  </p>
                  <Field label="Top message (HTML)">
                    <textarea
                      rows={6}
                      value={form.welcome_top_message_html}
                      onChange={(e) => set('welcome_top_message_html', e.target.value)}
                      className="input font-mono text-xs"
                      placeholder='<p style="margin:0 0 16px 0;font-size:16px;">Hey, thanks for subscribing from the Bagel Central page!</p>'
                    />
                  </Field>
                  {page &&
                    (page.welcome_subject || page.welcome_html || page.welcome_text) && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
                        <p className="text-amber-900 font-medium">
                          Legacy override (no longer used)
                        </p>
                        <p className="text-amber-800">
                          This page has welcome-email override fields from
                          the previous system. They are no longer read by
                          the send pipeline. Copy anything you want to keep
                          into the top-message field above; the legacy
                          values will stay on the row but inert.
                        </p>
                        {page.welcome_subject && (
                          <details className="text-amber-900">
                            <summary className="cursor-pointer">Legacy subject</summary>
                            <pre className="mt-1 whitespace-pre-wrap break-words text-amber-950">
                              {page.welcome_subject}
                            </pre>
                          </details>
                        )}
                        {page.welcome_html && (
                          <details className="text-amber-900">
                            <summary className="cursor-pointer">Legacy HTML</summary>
                            <pre className="mt-1 whitespace-pre-wrap break-words text-amber-950">
                              {page.welcome_html}
                            </pre>
                          </details>
                        )}
                        {page.welcome_text && (
                          <details className="text-amber-900">
                            <summary className="cursor-pointer">Legacy plain text</summary>
                            <pre className="mt-1 whitespace-pre-wrap break-words text-amber-950">
                              {page.welcome_text}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                </div>
              )}
            </div>

            {!isHomepage && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="active" className="text-sm text-slate-700">
                  Active (visible at /signup/{form.slug || '…'})
                </label>
              </div>
            )}

            <div className="flex items-center gap-2 pt-4">
              <button type="submit" disabled={saving} className="btn-primary">
                {saving
                  ? 'Saving…'
                  : isCreate
                  ? 'Create signup page'
                  : isHomepage
                  ? 'Save homepage'
                  : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/signups')}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label}
        {hint && <span className="ml-2 font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
